/*-----------------------------------------------------------------------------------*/
/* WISH LIST
/*
/* The wish list is for logged-in customers only. Saved products live in localStorage
/* under a key scoped to window.wishlistCustomerId (set in layout/theme.liquid), so two
/* accounts sharing a salon computer keep separate lists. Being localStorage, a list
/* still does not follow a customer to another browser or device.
/*
/* With no customer signed in the store is inert: nothing is read and nothing is
/* written. The Liquid side matches - snippets/wishlist-button.liquid and the header
/* icon render nothing for guests.
/*
/* Public API (window.PSBWishlist):
/*   items()          -> [{ handle, id, added_at }] newest first
/*   handles()        -> ['handle-a', 'handle-b']
/*   count()          -> Number
/*   has(handle)      -> Boolean
/*   add(item)        -> Boolean (false when it was already saved)
/*   remove(handle)   -> Boolean (false when it was not saved)
/*   toggle(item)     -> 'added' | 'removed'
/*   clear()          -> void
/*   syncUI(root)     -> re-applies saved state to buttons/counters inside root
/*
/* Emits a `wishlist:updated` CustomEvent on `document` after every change, with
/* detail: { items, action, handle }. `action` is 'add' | 'remove' | 'clear' | 'sync'.
/*-----------------------------------------------------------------------------------*/
;(function () {
	'use strict'

	var customerId = window.wishlistCustomerId || null
	var enabled = !!customerId
	var STORAGE_KEY = enabled ? 'psb:wishlist:' + customerId : 'psb:wishlist'
	var MAX_ITEMS = 250

	// localStorage throws in some privacy modes, so fall back to a session-only copy.
	var memoryFallback = null

	function readRaw() {
		if (memoryFallback !== null) return memoryFallback
		try {
			return window.localStorage.getItem(STORAGE_KEY)
		} catch (error) {
			return null
		}
	}

	function writeRaw(value) {
		try {
			window.localStorage.setItem(STORAGE_KEY, value)
		} catch (error) {
			// Quota exceeded or storage blocked - keep the list alive for this page view.
			memoryFallback = value
		}
	}

	function normalise(entry) {
		if (!entry) return null

		var handle = typeof entry === 'string' ? entry : entry.handle
		if (typeof handle !== 'string') return null

		handle = handle.trim()
		if (!handle) return null

		var id = entry && entry.id != null ? String(entry.id) : ''
		var addedAt = entry && entry.added_at ? Number(entry.added_at) : NaN

		return {
			handle: handle,
			id: id,
			added_at: isNaN(addedAt) ? Date.now() : addedAt,
		}
	}

	function items() {
		if (!enabled) return []

		var raw = readRaw()
		if (!raw) return []

		var parsed
		try {
			parsed = JSON.parse(raw)
		} catch (error) {
			return []
		}
		if (!Array.isArray(parsed)) return []

		var seen = {}
		var list = []

		for (var i = 0; i < parsed.length; i++) {
			var entry = normalise(parsed[i])
			if (!entry || seen[entry.handle]) continue
			seen[entry.handle] = true
			list.push(entry)
		}

		return list
	}

	function announce(list, action, handle) {
		syncUI()
		document.dispatchEvent(
			new CustomEvent('wishlist:updated', {
				detail: {
					items: list,
					action: action,
					handle: handle || null,
				},
			})
		)
	}

	function save(list, action, handle) {
		if (!enabled) return

		var trimmed = list.slice(0, MAX_ITEMS)
		writeRaw(JSON.stringify(trimmed))
		announce(trimmed, action, handle)
	}

	function handles() {
		return items().map(function (entry) {
			return entry.handle
		})
	}

	function has(handle) {
		if (typeof handle !== 'string' || !handle) return false
		return handles().indexOf(handle.trim()) !== -1
	}

	function add(entry) {
		if (!enabled) return false

		var item = normalise(entry)
		if (!item) return false

		var list = items()
		for (var i = 0; i < list.length; i++) {
			if (list[i].handle === item.handle) return false
		}

		// Newest first, so the wish list page reads like a recently-saved feed.
		list.unshift(item)
		save(list, 'add', item.handle)
		return true
	}

	function remove(entry) {
		if (!enabled) return false

		var item = normalise(entry)
		if (!item) return false

		var list = items()
		var next = list.filter(function (saved) {
			return saved.handle !== item.handle
		})
		if (next.length === list.length) return false

		save(next, 'remove', item.handle)
		return true
	}

	function toggle(entry) {
		if (!enabled) return null

		var item = normalise(entry)
		if (!item) return null

		if (has(item.handle)) {
			remove(item.handle)
			return 'removed'
		}

		add(item)
		return 'added'
	}

	function clear() {
		save([], 'clear')
	}

	function count() {
		return items().length
	}

	/*-------------------------------------------------------------------------------*/
	/* UI sync
	/*-------------------------------------------------------------------------------*/

	function syncButtons(root) {
		var saved = handles()
		var buttons = (root || document).querySelectorAll('[data-wishlist-toggle]')

		Array.prototype.forEach.call(buttons, function (button) {
			var handle = button.getAttribute('data-product-handle')
			var isSaved = saved.indexOf(handle) !== -1
			var label = isSaved
				? button.getAttribute('data-label-remove')
				: button.getAttribute('data-label-add')

			button.classList.toggle('is-in-wishlist', isSaved)
			button.setAttribute('aria-pressed', isSaved ? 'true' : 'false')

			if (label) {
				button.setAttribute('aria-label', label)
				button.setAttribute('title', label)

				var labelEl = button.querySelector('[data-wishlist-label]')
				if (labelEl) labelEl.textContent = label
			}
		})
	}

	function syncCounts(root) {
		var total = count()
		var counters = (root || document).querySelectorAll('[data-wishlist-count]')

		Array.prototype.forEach.call(counters, function (counter) {
			// Matches the cart bubble: empty content collapses the badge via CSS.
			counter.textContent = total > 0 ? String(total) : ''
		})

		var links = (root || document).querySelectorAll('[data-wishlist-link]')
		Array.prototype.forEach.call(links, function (link) {
			link.classList.toggle('has-wishlist-items', total > 0)
		})
	}

	function syncUI(root) {
		syncButtons(root)
		syncCounts(root)
	}

	/*-------------------------------------------------------------------------------*/
	/* Wiring
	/*-------------------------------------------------------------------------------*/

	function onClick(event) {
		if (!event.target.closest) return

		var button = event.target.closest('[data-wishlist-toggle]')
		if (!button) return

		// Product cards wrap their media in a link; don't let the toggle navigate away.
		event.preventDefault()
		event.stopPropagation()

		toggle({
			handle: button.getAttribute('data-product-handle'),
			id: button.getAttribute('data-product-id'),
		})
	}

	// Product grids on this theme are re-rendered by AJAX filtering and by the wish
	// list page itself, so newly injected buttons need their saved state applied.
	function observeInjectedButtons() {
		if (typeof MutationObserver === 'undefined') return

		var selector = '[data-wishlist-toggle], [data-wishlist-count]'
		var pending = null

		var observer = new MutationObserver(function (mutations) {
			if (pending) return

			var needsSync = mutations.some(function (mutation) {
				return Array.prototype.some.call(mutation.addedNodes, function (node) {
					if (node.nodeType !== 1) return false
					return node.matches(selector) || !!node.querySelector(selector)
				})
			})
			if (!needsSync) return

			pending = window.requestAnimationFrame(function () {
				pending = null
				syncUI()
			})
		})

		observer.observe(document.body, { childList: true, subtree: true })
	}

	function init() {
		// Guests get no wish list markup at all, so there is nothing to wire up.
		if (!enabled) return

		syncUI()
		document.addEventListener('click', onClick)
		observeInjectedButtons()

		// Keep other tabs of the same store in step.
		window.addEventListener('storage', function (event) {
			if (event.key !== STORAGE_KEY) return
			memoryFallback = null
			announce(items(), 'sync')
		})
	}

	window.PSBWishlist = {
		STORAGE_KEY: STORAGE_KEY,
		enabled: enabled,
		items: items,
		handles: handles,
		count: count,
		has: has,
		add: add,
		remove: remove,
		toggle: toggle,
		clear: clear,
		syncUI: syncUI,
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init)
	} else {
		init()
	}
})()
